import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BRANDING, FEATURE_FLAGS } from '../../constants/branding.constants';
import { BrandThemeService } from '../theme/brand-theme.service';

/**
 * Public branding info for the application.
 */
export interface BrandingInfo {
	id: number;
	slug: string;
	name: string;
	logo: string | null;
	appIcon: string | null;
	favicon: string | null;
	loginBackgroundImage: string | null;
	boostBackgroundImage: string | null;
	splashImage: string | null;
	primaryColor: string | null;
	secondaryColor: string | null;
	defaultLanguage: string;
	featureFlags: Record<string, boolean> | null;
	revolutId: string | null;
	bizumPhone: string | null;
}

const BELTRASHOW_BRANDING: BrandingInfo = {
	id: BRANDING.id,
	slug: BRANDING.slug,
	name: BRANDING.name,
	logo: BRANDING.logo,
	appIcon: BRANDING.appIcon,
	favicon: BRANDING.favicon,
	loginBackgroundImage: BRANDING.loginBackgroundImage,
	boostBackgroundImage: BRANDING.boostBackgroundImage,
	splashImage: BRANDING.splashImage,
	primaryColor: BRANDING.primaryColor,
	secondaryColor: BRANDING.secondaryColor,
	defaultLanguage: BRANDING.defaultLanguage,
	featureFlags: { ...FEATURE_FLAGS },
	revolutId: BRANDING.revolutId,
	bizumPhone: BRANDING.bizumPhone,
};

/**
 * Branding service.
 *
 * Returns hardcoded Beltrashow branding from constants and applies the
 * theme/favicon/title on construction.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
	private readonly platformId = inject(PLATFORM_ID);
	private currentBranding$ = new BehaviorSubject<BrandingInfo | null>(BELTRASHOW_BRANDING);

	constructor(private brandThemeService: BrandThemeService) {
		this.applyTheme(BELTRASHOW_BRANDING);
	}

	get branding$(): Observable<BrandingInfo | null> {
		return this.currentBranding$.asObservable();
	}

	get current(): BrandingInfo | null {
		return this.currentBranding$.getValue();
	}

	/**
	 * Returns the Beltrashow branding immediately. Kept as Observable for
	 * components that prefer to subscribe.
	 */
	load(): Observable<BrandingInfo> {
		return of(BELTRASHOW_BRANDING);
	}

	private applyTheme(branding: BrandingInfo): void {
		this.brandThemeService.applyBrandColors(branding.primaryColor, branding.secondaryColor);
		this.updateFavicon(branding.favicon);
		this.updateDocumentTitle(branding.name);
	}

	private updateFavicon(faviconUrl: string | null): void {
		if (!isPlatformBrowser(this.platformId) || !faviconUrl) return;
		const linkElement = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
		if (linkElement) {
			linkElement.href = faviconUrl;
		} else {
			const newLink = document.createElement('link');
			newLink.rel = 'icon';
			newLink.href = faviconUrl;
			document.head.appendChild(newLink);
		}
	}

	private updateDocumentTitle(brandingName: string): void {
		if (!isPlatformBrowser(this.platformId)) return;
		document.title = brandingName;
	}
}
